import { PaymentMethod } from '@stripe/stripe-js';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { CLOUD_FUNCTIONS } from '../auth/FirebaseContext';
import { StripeCustomer } from '../models/Stripe';
import {
  EndSessionCallback,
  useExpiredSessionErrorToNavigate,
} from '../pages/dashboard/useExpiredSessionErrorToNavigate';
import { sendToSentry, sentryStripeFingerprint } from '../utils/mrr/sentryReporter';
import { cloud_getCustomerPaymentMethods } from '../utils/mrr/cloudFunctions';

interface UsePaymentMethodsProps {
  customer?: StripeCustomer;
  defaultPaymentMethods?: string[];
  unique?: boolean;
}

const usePaymentMethods = (
  { customer, defaultPaymentMethods, unique = true }: UsePaymentMethodsProps,
  skip?: boolean
) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const handleExpiredSession = useExpiredSessionErrorToNavigate();
  const [refresh, setRefresh] = useState(0);
  const refreshPaymentMethods = () => {
    setRefresh((prev) => prev + 1);
  };
  useEffect(() => {
    if (!customer || skip) return;
    const fetchPaymentMethods = async () => {
      setLoading(true);
      try {
        // Fetch the payment methods from Stripe
        const methods = await getPaymentMethodsForCustomer(customer.id, handleExpiredSession);
        const uniquePaymentMethods = (() => {
          const seen = new Set<string>();
          // sorted by created date to keep the most recent payment method
          return methods.sort((a, b) => a.created - b.created).reduce((acc, method) => {
            const isAllowed = defaultPaymentMethods
              ? defaultPaymentMethods.includes(method.type)
              : method.type === 'card' || method.type === 'us_bank_account';

            if (!isAllowed) return acc;
            if (!unique) {
              acc.push(method);
              return acc;
            }
            // Generate a unique key per method
            let key = '';
            if (method.type === 'card') {
              const { last4, exp_month, exp_year } = method.card || {};
              key = `card:${last4}:${exp_month}:${exp_year}`;
            } else if (method.type === 'us_bank_account') {
              const { last4, routing_number } = method.us_bank_account || {};
              key = `us_bank:${last4}:${routing_number}`;
            } else {
              // Not handling other types
              return acc;
            }

            if (!seen.has(key)) {
              seen.add(key);
              acc.push(method);
            }

            return acc;
          }, [] as typeof methods);
        })();

        setPaymentMethods(
          uniquePaymentMethods.sort((a, b) => {
            if (a.id === customer.invoice_settings.default_payment_method) return -1;
            if (b.id === customer.invoice_settings.default_payment_method) return 1;
            return a.created - b.created;
          })
        );

        // Set default payment method if one exists
        if (methods.length > 0) {
          const defaultMethod = uniquePaymentMethods.find(
            (method) => method.id === customer.invoice_settings.default_payment_method
          );
          if (defaultMethod) {
            setSelectedPaymentMethodId(defaultMethod.id);
            setDefaultPaymentMethodId(defaultMethod.id);
          }
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentMethods();
  }, [customer, skip, handleExpiredSession, defaultPaymentMethods, refresh, unique]);

  const selectPaymentMethod = (paymentMethodId: string) => {
    // console.log('Payment method selected: ', paymentMethodId);
    setSelectedPaymentMethodId(paymentMethodId);
  };

  return {
    defaultPaymentMethodId,
    paymentMethods,
    selectedPaymentMethodId,
    selectPaymentMethod,
    loading,
    error,
    refreshPaymentMethods,
  };
};

async function getPaymentMethodsForCustomer(
  customerId: string,
  handleExpiredSession: EndSessionCallback
): Promise<PaymentMethod[]> {
  const getCustomerPaymentMethods = httpsCallable(CLOUD_FUNCTIONS, cloud_getCustomerPaymentMethods);
  const doGetCustomerPaymentMethods = async () => {
    return getCustomerPaymentMethods({ customerId })
      .then((result: any) => {
        return result.data;
      })
      .catch((e: any) => {
        handleExpiredSession(e);
        sendToSentry(e, null, sentryStripeFingerprint, cloud_getCustomerPaymentMethods);
        throw new Error(e.message);
      });
  };
  const customerPaymentMethods = await doGetCustomerPaymentMethods();
  return customerPaymentMethods;
}

export default usePaymentMethods;
